import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { unitAPI, adminAPI, settingsAPI } from '../services/api';
import websocketService from '../services/websocket';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { canManageUnitImport } from '../utils/unitImportPermissions';
import { hasAdminPermission } from '../utils/adminPermissions';
import { formatLocalizedDateTime, normalizeAutogeneratedDeviceName } from '../utils/i18nFormatters';
import { formatStationDisplayId } from '../utils/stationId';
import { buildWaterPriceConfiguration } from '../utils/waterPriceLimits';
import { splitPhilippineAddress } from '../utils/philippineAddress';
import { parseDeviceImport, validateSupplierId } from '../utils/deviceImport';
import {
  getUnitPartnerDisplay,
  getUnitRegionalPartnerDisplay,
  getUnitStewardDisplay
} from '../utils/unitParties';
import {
  getPresenceLastDataAt,
  getUnitConnectionStatus,
  mergeDevicePresenceEvent,
  mergePolledUnits
} from '../utils/devicePresenceMerge';
import {
  Monitor,
  AlertTriangle,
  Search,
  Wifi,
  WifiOff,
  Upload,
  X,
  Loader2,
  CheckCircle2,
  Save,
  Edit,
  CalendarDays,
  MapPin,
  Wand2
} from 'lucide-react';
import { AdminModalSurface } from './AdminModal';

const UNIT_STATUS_REFRESH_MS = Number(process.env.REACT_APP_UNIT_STATUS_REFRESH_MS || 60000);

const getUnitOpenAlertCount = (unit) => Number(unit?.openAlertCount || unit?.alertSummary?.openCount || 0);
const getUnitLatestOpenAlert = (unit) => unit?.latestOpenAlert || unit?.alertSummary?.latestOpenAlert || null;
const hasUnitOpenAlert = (unit) => Boolean(unit?.hasOpenAlert || getUnitOpenAlertCount(unit) > 0);

const formatInspectionDateTime = (value, language) => {
  if (!value) {
    return null;
  }

  const formatted = formatLocalizedDateTime(value, language);
  return formatted === '-' ? null : formatted;
};

const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const getPart = (type) => parts.find((part) => part.type === type)?.value;
  const year = getPart('year');
  const month = getPart('month');
  const day = getPart('day');

  return year && month && day ? `${year}-${month}-${day}` : '';
};

export default function UnitControl() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const canEditUnits = canManageUnitImport(user);
  const canManageUnitPricing = hasAdminPermission(user, 'manage_unit_pricing');
  const canOpenUnitEditor = canEditUnits || canManageUnitPricing;
  const canManagePartnerBindings = hasAdminPermission(user, 'manage_partner_bindings');
  const canManageSim = hasAdminPermission(user, 'manage_sim_cards');
  const canViewSim = hasAdminPermission(user, 'view_sim_cards');
  const defaultEditForm = {
    rpOwnerId: '',
    puskudId: '',
    stewardId: '',
    installedAt: '',
    simCardNumber: '',
    simPaidUntil: '',
    simSupplier: '',
    protocolDId: '',
    legacyQrAlias: '',
    location: '',
    provinceCode: '',
    provinceName: '',
    cityName: '',
    districtName: '',
    addressDetail: '',
    mineralWaterPrice: '',
    pureWaterPrice: ''
  };
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchProps] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [regionalPartnerFilter, setRegionalPartnerFilter] = useState('all');
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importDeviceNo, setImportDeviceNo] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [identityDiagnostics, setIdentityDiagnostics] = useState([]);
  const [diagnosticsError, setDiagnosticsError] = useState(false);
  useEffect(() => {
    if (!showImportDialog || !canViewSim) return;
    let active = true;
    unitAPI.getIdentityDiagnostics().then((response) => {
      if (active) { setIdentityDiagnostics(response.data?.data || []); setDiagnosticsError(false); }
    }).catch(() => { if (active) setDiagnosticsError(true); });
    return () => { active = false; };
  }, [showImportDialog, canViewSim]);

  // Edit unit detail dialog.
  const [showEditUnitDialog, setShowEditUnitDialog] = useState(false);
  const [selectedUnitForEdit, setSelectedUnitForEdit] = useState(null);
  const [editForm, setEditForm] = useState(defaultEditForm);
  const [kudSearchQuery, setKudSearchQuery] = useState('');
  const [kudOptions, setKudOptions] = useState([]);
  const [puskudSearchQuery, setPuskudSearchQuery] = useState('');
  const [puskudOptions, setPuskudOptions] = useState([]);
  const [stewardSearchQuery, setStewardSearchQuery] = useState('');
  const [stewardOptions, setStewardOptions] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [waterPriceConfiguration, setWaterPriceConfiguration] = useState(null);
  const [waterPriceConfigurationState, setWaterPriceConfigurationState] = useState('idle');
  const mineralWaterPriceLimits = waterPriceConfiguration?.limits.mineral;
  const pureWaterPriceLimits = waterPriceConfiguration?.limits.pure;
  const formatWaterPrice = (value) => {
    const numericValue = Number(value);
    if (value === null || value === undefined || value === '' || Number.isNaN(numericValue)) {
      return t('units.table.priceNotSet');
    }

    return `₱${numericValue.toFixed(0)}/L`;
  };

  const formatStationId = (unit) => formatStationDisplayId(unit);

  const formatListDeviceName = (unit) => {
    const stationId = formatStationId(unit);
    const displayName = normalizeAutogeneratedDeviceName(unit.deviceName, stationId);
    const fullDeviceId = String(unit.deviceId || '').trim();
    if (!displayName || displayName === stationId || displayName === fullDeviceId || /^\d{7,}$/.test(displayName)) {
      return '';
    }
    return displayName;
  };

  const getUnitPartner = getUnitPartnerDisplay;
  const getUnitRegionalPartner = getUnitRegionalPartnerDisplay;
  const getUnitSteward = getUnitStewardDisplay;

  const renderPartyCell = (party) => (
    <div className="min-w-[120px]">
      <div className={`text-xs font-semibold ${party?.name || party?.phone ? 'text-slate-200' : 'text-slate-500'}`}>
        {party?.name || party?.phone || t('units.table.notAssigned')}
      </div>
      {party?.name && party?.phone && (
        <div className="mt-1 text-[10px] text-slate-500">{party.phone}</div>
      )}
    </div>
  );

  const buildUnitSearchText = (unit) => [
    unit.id,
    formatStationId(unit),
    unit.deviceId,
    unit.deviceName,
    unit.stationId,
    unit.unitCode,
    unit.deviceNo,
    unit.rpOwnerId,
    unit.rpOwner?.name,
    unit.rpOwner?.phoneNumber,
    unit.rpOwner?.phone,
    unit.partner?.name,
    unit.partner?.phoneNumber,
    unit.partner?.phone,
    unit.kudOwner?.name,
    unit.kudOwner?.phoneNumber,
    unit.kudOwner?.phone,
    unit.partnerName,
    unit.kudOwnerName,
    unit.puskudId,
    unit.puskud?.name,
    unit.puskud?.phoneNumber,
    unit.puskud?.phone,
    unit.regionalPartner?.name,
    unit.regionalPartner?.phoneNumber,
    unit.regionalPartner?.phone,
    unit.puskudOwner?.name,
    unit.puskudOwner?.phoneNumber,
    unit.puskudOwner?.phone,
    unit.puskudName,
    unit.regionalPartnerName,
    unit.puskudOwnerName,
    unit.stewardId,
    unit.steward?.name,
    unit.steward?.phone,
    unit.steward?.phoneNumber,
    unit.location,
    unit.provinceName,
    unit.cityName,
    unit.districtName,
    unit.addressDetail,
    unit.installedAt
  ].map((value) => String(value || '').toLowerCase()).join(' ');

  const formatLastHeartbeat = (value) => {
    if (!value) {
      return t('unitControl.lastSeenNever', { defaultValue: 'No heartbeat yet' });
    }

    const heartbeatDate = new Date(value);
    if (Number.isNaN(heartbeatDate.getTime())) {
      return t('unitControl.lastSeenUnknown', { defaultValue: 'Last seen unknown' });
    }

    const diffMs = Date.now() - heartbeatDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) {
      return t('unitControl.lastSeenJustNow', { defaultValue: 'Just now' });
    }

    if (diffMinutes < 60) {
      return t('unitControl.lastSeenMinutes', {
        count: diffMinutes,
        defaultValue: `${diffMinutes}m ago`
      });
    }

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return t('unitControl.lastSeenHours', {
        count: diffHours,
        defaultValue: `${diffHours}h ago`
      });
    }

    const diffDays = Math.floor(diffHours / 24);
    return t('unitControl.lastSeenDays', {
      count: diffDays,
      defaultValue: `${diffDays}d ago`
    });
  };

  const validateUnitPrice = (value, waterTypeLabel, limits) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return t('unitControl.priceMustBeNumber', { waterType: waterTypeLabel });
    }

    if (numericValue < limits.min || numericValue > limits.max) {
      return t('unitControl.priceRangeError', {
        waterType: waterTypeLabel,
        min: limits.min,
        max: limits.max
      });
    }

    return null;
  };

  const normalizePriceInput = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? String(numericValue) : String(value).trim();
  };

  const handleDeviceUpdate = useCallback((data) => {
    console.log('[UnitControl] Device update received:', data);
    setUnits(prevUnits => {
      const deviceId = data?.deviceId || data?.data?.deviceId;
      if (!deviceId) return prevUnits;
      const current = prevUnits.find((unit) => unit.deviceId === deviceId);
      const sameRevision = current
        && current.version === data.data?.version
        && current.detectedAt === data.data?.detectedAt
        && current.presenceState === data.data?.presenceState;
      const merged = mergeDevicePresenceEvent(prevUnits, data);
      if (sameRevision) return merged;
      return merged.map((unit) => (
        unit.deviceId === deviceId ? { ...unit, _justUpdated: true } : unit
      ));
    });

    const deviceId = data?.deviceId || data?.data?.deviceId;
    if (deviceId) {
      setTimeout(() => {
        setUnits(units => units.map(u => ({
          ...u,
          _justUpdated: u.deviceId === deviceId ? false : u._justUpdated
        })));
      }, 500);
    }
  }, []);

  // Handle WebSocket connection state.
  const handleWsConnected = useCallback(() => {
    setWsStatus('connected');
    console.log('[UnitControl] WebSocket connected');
  }, []);

  const handleWsDisconnected = useCallback(() => {
    setWsStatus('disconnected');
    console.log('[UnitControl] WebSocket disconnected');
  }, []);

  // Initialize WebSocket listeners.
  useEffect(() => {
    setWsStatus(websocketService.getConnectionState());
    websocketService.on('device_update', handleDeviceUpdate);
    websocketService.on('device_presence_changed', handleDeviceUpdate);
    websocketService.on('connected', handleWsConnected);
    websocketService.on('disconnected', handleWsDisconnected);

    return () => {
      websocketService.off('device_update', handleDeviceUpdate);
      websocketService.off('device_presence_changed', handleDeviceUpdate);
      websocketService.off('connected', handleWsConnected);
      websocketService.off('disconnected', handleWsDisconnected);
    };
  }, [handleDeviceUpdate, handleWsConnected, handleWsDisconnected]);

  const loadUnits = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) setLoading(true);
    try {
      const res = await unitAPI.getUnits();
      if (res.data && res.data.success) {
        const polledUnits = res.data.data || [];
        setUnits((currentUnits) => mergePolledUnits(currentUnits, polledUnits));
        websocketService.syncDeviceSubscriptions(polledUnits.map((unit) => unit.deviceId));
      }
    } catch (error) {
      console.error('Failed to load units:', error);
      if (!silent) setUnits([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUnits();
    return () => websocketService.syncDeviceSubscriptions([]);
  }, [loadUnits]);

  useEffect(() => {
    if (!canManageUnitPricing) {
      setWaterPriceConfiguration(null);
      setWaterPriceConfigurationState('idle');
      return undefined;
    }

    let active = true;
    setWaterPriceConfigurationState('loading');
    settingsAPI.getUnitWaterPriceConfiguration()
      .then((response) => {
        if (!active) return;
        const nextConfiguration = buildWaterPriceConfiguration(response.data?.data);
        setWaterPriceConfiguration(nextConfiguration);
        setWaterPriceConfigurationState(nextConfiguration ? 'ready' : 'error');
      })
      .catch((error) => {
        console.error('[UnitControl] Failed to load water price configuration:', error);
        if (!active) return;
        setWaterPriceConfiguration(null);
        setWaterPriceConfigurationState('error');
      });

    return () => {
      active = false;
    };
  }, [canManageUnitPricing]);

  useEffect(() => {
    const refreshMs = Number.isFinite(UNIT_STATUS_REFRESH_MS)
      ? Math.max(UNIT_STATUS_REFRESH_MS, 10000)
      : 60000;
    const interval = setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        loadUnits({ silent: true });
      }
    }, refreshMs);

    return () => clearInterval(interval);
  }, [loadUnits]);

  const downloadImportTemplate = () => {
    const csv = 'deviceNo,password,deviceName\n8985200015509541348F,,Station A\n00VendorCode0001,,Station B\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'atmwater-device-import-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleRegisterDevice = async () => {
    if (!canEditUnits || importLoading) return;
    let rows;
    try { rows = parseDeviceImport(importDeviceNo); }
    catch (error) { setImportMessage({ type: 'error', text: error.message }); return; }
    if (rows.length === 0) {
      setImportMessage({ type: 'error', text: t('unitControl.enterDeviceNo') });
      return;
    }

    if (importPreview?.source !== importDeviceNo) {
      setImportPreview({ source: importDeviceNo, rows });
      setImportMessage(null);
      return;
    }
    setImportLoading(true);
    setImportMessage(null);

    const result = { success: 0, exists: 0, failed: [] };
    for (const row of rows) {
      try {
        const res = await unitAPI.registerDevice(row);
        if (res.data?.success && res.data.alreadyExists) {
          result.exists += 1;
        } else if (res.data?.success) {
          result.success += 1;
        } else {
          result.failed.push(`${row.deviceNo}: ${res.data?.message || t('unitControl.importFailed')}`);
        }
      } catch (error) {
        console.error('Device registration failed', { status: error.response?.status, code: error.response?.data?.code });
        const message = error.response?.status === 404
          ? t('unitControl.deviceNotFound')
          : (error.response?.data?.message || t('unitControl.importError'));
        result.failed.push(`${row.deviceNo}: ${message}`);
      }
    }

    const summary = t('unitControl.importSummary', {
      success: result.success,
      exists: result.exists,
      failed: result.failed.length
    });
    const failedText = result.failed.length > 0
      ? ` ${t('unitControl.importFailedItems', { items: result.failed.slice(0, 3).join('; ') })}`
      : '';
    setImportMessage({
      type: result.failed.length > 0 ? 'error' : (result.exists > 0 ? 'info' : 'success'),
      text: `${summary}${failedText}`
    });
    if (result.failed.length === 0) {
      setImportDeviceNo('');
      setImportPreview(null);
    }
    await loadUnits();
    setImportLoading(false);
  };

  // Search steward users.
  const searchStewardUsers = async (query) => {
    setStewardSearchQuery(query);
    if (!query.trim()) {
      setStewardOptions([]);
      return;
    }
    try {
      const res = await adminAPI.searchUsers(query, ['Steward']);
      if (res.data?.success) {
        setStewardOptions(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to search Steward users:', error);
      setStewardOptions([]);
    }
  };

  const searchKudUsers = async (query) => {
    setKudSearchQuery(query);
    if (!query.trim()) {
      setKudOptions([]);
      return;
    }
    try {
      const res = await adminAPI.searchUsers(query, ['KUD']);
      if (res.data?.success) {
        setKudOptions(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to search Partner PA users:', error);
      setKudOptions([]);
    }
  };

  const searchPuskudUsers = async (query) => {
    setPuskudSearchQuery(query);
    if (!query.trim()) {
      setPuskudOptions([]);
      return;
    }
    try {
      const res = await adminAPI.searchUsers(query, ['PUSKUD']);
      if (res.data?.success) {
        setPuskudOptions(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to search Regional Partner RP users:', error);
      setPuskudOptions([]);
    }
  };

  const getUserDisplayName = (user) => String(
    user?.name || user?.nickname || user?.fullName || user?.phoneNumber || user?.phone || `#${user?.id || ''}`
  ).trim();

  const getUserAccount = (user) => String(
    user?.account || user?.username || user?.email || user?.login || user?.phoneNumber || user?.phone || ''
  ).trim();

  const getUserRoleSummary = (user) => {
    const fromRoles = Array.isArray(user?.roles)
      ? user.roles.map((role) => (typeof role === 'string' ? role : role?.name || role?.role || role?.code))
      : [];
    const directRoles = [user?.role, user?.userRole, user?.userType].filter(Boolean);
    const uniqueRoles = Array.from(new Set([...fromRoles, ...directRoles].map((role) => String(role || '').trim()).filter(Boolean)));
    return uniqueRoles.join(', ');
  };

  // Open the edit modal.
  const handleEditUnit = (unit) => {
    if (!canOpenUnitEditor) return;
    const partner = getUnitPartner(unit);
    const regionalPartner = getUnitRegionalPartner(unit);
    const steward = getUnitSteward(unit);
    const trueInstalledAt = unit.installationSource === 'installed_at'
      ? formatDateInput(unit.installedAt)
      : '';

    setSelectedUnitForEdit(unit);
    setEditForm({
      rpOwnerId: unit.rpOwnerId || '',
      puskudId: unit.puskudId || '',
      stewardId: unit.stewardId || '',
      installedAt: trueInstalledAt,
      simCardNumber: unit.simCardNumber || '',
      simPaidUntil: formatDateInput(unit.simPaidUntil),
      simSupplier: unit.simSupplier || '',
      protocolDId: unit.protocolDId || unit.simCardNumber || '',
      legacyQrAlias: '',
      location: unit.location || '',
      provinceCode: unit.provinceCode || '',
      provinceName: unit.provinceName || '',
      cityName: unit.cityName || '',
      districtName: unit.districtName || '',
      addressDetail: unit.addressDetail || '',
      mineralWaterPrice: unit.mineralWaterPrice || '',
      pureWaterPrice: unit.pureWaterPrice || ''
    });
    setKudSearchQuery(partner?.name ? `${partner.name}${unit.rpOwnerId ? ` (ID: ${unit.rpOwnerId})` : ''}` : '');
    setKudOptions([]);
    setPuskudSearchQuery(regionalPartner?.name ? `${regionalPartner.name}${unit.puskudId ? ` (ID: ${unit.puskudId})` : ''}` : '');
    setPuskudOptions([]);
    setStewardSearchQuery(steward?.name ? `${steward.name}${unit.stewardId ? ` (ID: ${unit.stewardId})` : ''}` : '');
    setStewardOptions([]);
    setShowEditUnitDialog(true);
  };

  const handleApplyAddressSplit = (address = editForm.location) => {
    const parsed = splitPhilippineAddress(address);
    setEditForm((form) => ({
      ...form,
      provinceCode: '',
      provinceName: parsed.provinceName,
      cityName: parsed.cityName,
      districtName: parsed.districtName,
      addressDetail: parsed.addressDetail
    }));
  };

  // Save edits.
  const handleSaveUnitDetails = async () => {
    if (!selectedUnitForEdit || editLoading) return;

    const updatePayload = {
      location: editForm.location,
      provinceCode: editForm.provinceCode || null,
      provinceName: editForm.provinceName || null,
      cityName: editForm.cityName || null,
      districtName: editForm.districtName || null,
      addressDetail: editForm.addressDetail || null
    };
    if (canManagePartnerBindings) {
      updatePayload.kudOwnerId = editForm.rpOwnerId || null;
      updatePayload.puskudId = editForm.puskudId || null;
      updatePayload.stewardId = editForm.stewardId || null;
    }
    const originalInstalledAt = selectedUnitForEdit.installationSource === 'installed_at'
      ? formatDateInput(selectedUnitForEdit.installedAt)
      : '';
    if (editForm.installedAt !== originalInstalledAt) {
      updatePayload.installedAt = editForm.installedAt || null;
    }
    if (canManageSim) {
      for (const key of ['simCardNumber', 'simSupplier', 'protocolDId', 'simPaidUntil']) {
        const original = key === 'simPaidUntil' ? formatDateInput(selectedUnitForEdit[key]) : (selectedUnitForEdit[key] || '');
        if (editForm[key] !== original) updatePayload[key] = editForm[key] || null;
      }
      if (editForm.legacyQrAlias) updatePayload.legacyQrAlias = editForm.legacyQrAlias;
      if (Object.keys(updatePayload).some((key) => key.startsWith('sim') || ['protocolDId', 'legacyQrAlias'].includes(key))) {
        try {
          if (selectedUnitForEdit.simSchemaReady !== true) throw new Error(t('unitControl.simUnavailable'));
          if (Object.keys(updatePayload).some((key) => key.startsWith('sim') || key === 'protocolDId')) {
            validateSupplierId(editForm.simCardNumber); validateSupplierId(editForm.simSupplier); validateSupplierId(editForm.protocolDId);
          }
        } catch (error) { alert(error.message); return; }
      }
    }

    setEditLoading(true);
    try {
      const res = await unitAPI.updateUnitDetail(selectedUnitForEdit.id, updatePayload);
      if (res.data?.success) {
        if (Object.keys(updatePayload).some((key) => key.startsWith('sim') || ['protocolDId', 'legacyQrAlias'].includes(key))) {
          const verified = (await unitAPI.getUnitDetail(selectedUnitForEdit.id)).data?.data;
          if (!verified || ['simCardNumber', 'simSupplier', 'protocolDId', 'simPaidUntil'].some((key) => Object.hasOwn(updatePayload, key) && (verified[key] || null) !== updatePayload[key])
            || (updatePayload.legacyQrAlias && !verified.legacyQrAliases?.includes(updatePayload.legacyQrAlias))) {
            throw new Error(t('unitControl.simReadbackFailed'));
          }
        }
        alert(res.data?.message || t('unitControl.updateSuccess') || 'Unit updated successfully');
        setShowEditUnitDialog(false);
        loadUnits();
      }
    } catch (error) {
      const responseData = error.response?.data || {};
      if (responseData.code === 'UNIT_INSTALLATION_SCHEMA_PENDING') {
        alert(t('unitControl.installedAtSchemaPending', {
          defaultValue: 'Installation date cannot be saved yet. Ask backend to run the approved unit installation migration on dev first.'
        }));
      } else if (responseData.code === 'UNIT_SIM_SCHEMA_PENDING') {
        alert(t('unitControl.simUnavailable'));
      } else {
        alert(responseData.message || t('unitControl.updateFailed', {
          defaultValue: `Failed to update unit: ${error.message}`
        }));
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveUnitPricing = async () => {
    if (!selectedUnitForEdit) return;

    if (!mineralWaterPriceLimits || !pureWaterPriceLimits) {
      alert(t('unitControl.pricingConfigUnavailable', {
        defaultValue: 'Water-price limits are unavailable. Configure and save the 20 PHP amounts in System Settings, then reload this page.'
      }));
      return;
    }

    const mineralPriceError = validateUnitPrice(editForm.mineralWaterPrice, t('units.table.mineralWater'), mineralWaterPriceLimits);
    if (mineralPriceError) {
      alert(mineralPriceError);
      return;
    }

    const purePriceError = validateUnitPrice(editForm.pureWaterPrice, t('units.table.pureWater'), pureWaterPriceLimits);
    if (purePriceError) {
      alert(purePriceError);
      return;
    }

    const updatePayload = {};
    if (normalizePriceInput(editForm.mineralWaterPrice) !== normalizePriceInput(selectedUnitForEdit.mineralWaterPrice)) {
      updatePayload.mineralWaterPrice = editForm.mineralWaterPrice;
    }
    if (normalizePriceInput(editForm.pureWaterPrice) !== normalizePriceInput(selectedUnitForEdit.pureWaterPrice)) {
      updatePayload.pureWaterPrice = editForm.pureWaterPrice;
    }
    if (Object.keys(updatePayload).length === 0) {
      alert(t('unitControl.noPricingChanges', { defaultValue: 'No water-price changes to save.' }));
      return;
    }

    setEditLoading(true);
    try {
      const res = await unitAPI.updateUnitPricing(selectedUnitForEdit.id, updatePayload);
      if (res.data?.success) {
        alert(res.data?.message || t('unitControl.pricingUpdateSuccess', { defaultValue: 'Water pricing updated successfully.' }));
        setSelectedUnitForEdit((unit) => unit ? {
          ...unit,
          mineralWaterPrice: updatePayload.mineralWaterPrice ?? unit.mineralWaterPrice,
          pureWaterPrice: updatePayload.pureWaterPrice ?? unit.pureWaterPrice
        } : unit);
        loadUnits();
      }
    } catch (error) {
      alert(error.response?.data?.message || t('unitControl.updateFailed', {
        defaultValue: `Failed to update unit: ${error.message}`
      }));
    } finally {
      setEditLoading(false);
    }
  };

  const partnerFilterOptions = Array.from(new Set(
    units.map((unit) => getUnitPartner(unit)?.name).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));
  const regionalPartnerFilterOptions = Array.from(new Set(
    units.map((unit) => getUnitRegionalPartner(unit)?.name).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));

  const filteredUnits = units.filter(u => {
    const search = searchTerm.trim().toLowerCase();
    const partnerName = getUnitPartner(u)?.name || '';
    const regionalPartnerName = getUnitRegionalPartner(u)?.name || '';
    const matchesSearch = !search || buildUnitSearchText(u).includes(search);
    const matchesPartner = partnerFilter === 'all' || partnerName === partnerFilter;
    const matchesRegionalPartner = regionalPartnerFilter === 'all' || regionalPartnerName === regionalPartnerFilter;
    return matchesSearch && matchesPartner && matchesRegionalPartner;
  });

  const renderUnitStatusCell = (unit) => {
    const connectionStatus = getUnitConnectionStatus(unit);
    const openAlertCount = getUnitOpenAlertCount(unit);
    const latestOpenAlert = getUnitLatestOpenAlert(unit);
    const warning = hasUnitOpenAlert(unit);
    const connectionOnline = connectionStatus === 'Online';
    const connectionUnknown = connectionStatus === 'Unknown' || connectionStatus === 'StagingNotConnected';
    const connectionPillClass = connectionOnline
      ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
      : connectionUnknown
        ? 'bg-slate-500/10 text-slate-300 border border-slate-500/30'
        : 'bg-red-500/10 text-red-400 border border-red-500/30';
    const connectionDotClass = connectionOnline
      ? 'bg-emerald-400'
      : connectionUnknown ? 'bg-slate-400' : 'bg-red-400';
    const pillClass = warning
      ? 'bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20'
      : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30';
    const dotClass = warning ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400';
    const label = warning
      ? t('units.status.Warning', { defaultValue: 'Warning' })
      : t('units.status.Normal', { defaultValue: 'Normal' });
    const alertHref = latestOpenAlert?.id
      ? `/device-alerts?status=open&unitId=${unit.id}&alertId=${latestOpenAlert.id}`
      : `/device-alerts?status=open&unitId=${unit.id}`;
    const statusPill = (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${pillClass}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`}></span>
        {label}
        {warning && openAlertCount > 0 && <span className="font-mono">({openAlertCount})</span>}
      </span>
    );

    return (
      <div className="space-y-2">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${connectionPillClass}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${connectionDotClass}`}></span>
          {t(`units.status.${connectionStatus}`, { defaultValue: connectionStatus })}
        </span>
        {warning ? (
          <Link
            to={alertHref}
            className="inline-flex"
            title={t('unitControl.openAlertDetail', { defaultValue: 'Open alert detail' })}
          >
            {statusPill}
          </Link>
        ) : statusPill}
        {warning && latestOpenAlert && (
          <div className="max-w-[180px] truncate text-[10px] text-amber-200" title={latestOpenAlert.alertMessage || latestOpenAlert.errorCode || latestOpenAlert.alertType}>
            {latestOpenAlert.errorCode || latestOpenAlert.alertType || t('unitControl.activeAlert', { defaultValue: 'Active alert' })}
          </div>
        )}
        <div className="text-[10px] text-slate-500">
          {t('unitControl.connectionLabel', { defaultValue: 'Connection' })}: {t(`units.status.${connectionStatus}`, { defaultValue: connectionStatus })}
        </div>
        {unit.stagingRuntimeIsolated && (
          <div className="max-w-[180px] text-[10px] text-amber-500">
            {t('unitControl.stagingIsolationHint', { defaultValue: 'Staging does not accept device TCP connections.' })}
          </div>
        )}
        <div className="text-[10px] text-slate-500">
          {t('unitControl.lastDataLabel', { defaultValue: 'Last data' })}: {formatLastHeartbeat(getPresenceLastDataAt(unit))}
        </div>
        <div className="text-[10px] text-slate-500">
          {t('unitControl.lastDisconnectedLabel', { defaultValue: 'Disconnected' })}: {formatLastHeartbeat(unit.lastDisconnectedAt)}
        </div>
        <div className="max-w-[180px] truncate text-[10px] text-slate-500" title={unit.connectionDisplaySource || unit.statusSource || 'unknown'}>
          {t('unitControl.statusSourceLabel', { defaultValue: 'Source' })}: {t(`unitControl.statusSources.${unit.connectionDisplaySource || unit.statusSource || 'unknown'}`, { defaultValue: unit.connectionDisplaySource || unit.statusSource || 'unknown' })}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-8 space-y-8">
      <header className="flex flex-col gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white flex items-center gap-3 whitespace-nowrap">
            <Monitor className="text-cyan-400" size={32} />
            {t('units.title')}
          </h2>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-slate-400 text-sm">
              <Trans i18nKey="units.totalUnits" count={units.length}>
                Total <span className="text-cyan-400 font-bold">{{count: units.length}}</span> Units Monitoring
              </Trans>
            </p>
            {/* WebSocket connection status */}
            {wsStatus === 'connected' ? (
              <span className="flex items-center gap-1.5 text-xs text-emerald-500">
                <Wifi size={12} />
                {t('unitControl.wsOnline')}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs text-amber-400" role="status">
                <WifiOff size={12} />
                {t('unitControl.wsReconnecting', { defaultValue: 'Realtime reconnecting; polling remains active' })}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              type="text"
              placeholder={t('units.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchProps(e.target.value)}
              className="bg-slate-800 border border-white/10 rounded-lg py-2 pl-10 pr-4 text-sm text-white focus:border-cyan-400 outline-none w-full sm:w-80"
            />
          </div>
          <select
            value={partnerFilter}
            onChange={(event) => setPartnerFilter(event.target.value)}
            className="bg-slate-800 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:border-cyan-400 outline-none"
          >
            <option value="all">{t('unitControl.filters.allPartners')}</option>
            {partnerFilterOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <select
            value={regionalPartnerFilter}
            onChange={(event) => setRegionalPartnerFilter(event.target.value)}
            className="bg-slate-800 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:border-cyan-400 outline-none"
          >
            <option value="all">{t('unitControl.filters.allRegionalPartners')}</option>
            {regionalPartnerFilterOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          {canEditUnits && (
            <button
              onClick={() => setShowImportDialog(true)}
              className="bg-slate-700 hover:bg-slate-600 text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all"
            >
              <Upload size={16} />
              {t('unitControl.importDevice')}
            </button>
          )}
          <span className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-400">
            <Wifi size={14} className="text-cyan-400" />
            {t('unitControl.autoRefresh')}
          </span>
        </div>
      </header>

      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-slate-800/50">
        <table className="min-w-[1080px] w-full text-left border-collapse">
          <thead className="bg-slate-900/50 text-xs uppercase text-slate-500 font-bold">
            <tr>
              <th className="p-4 border-b border-white/5">{t('units.table.unitId')}</th>
              <th className="p-4 border-b border-white/5">{t('units.table.partner')}</th>
              <th className="p-4 border-b border-white/5">{t('units.table.regionalPartner')}</th>
              <th className="p-4 border-b border-white/5">{t('units.table.steward')}</th>
              <th className="p-4 border-b border-white/5">{t('units.table.waterPricing')}</th>
              <th className="p-4 border-b border-white/5">{t('units.table.status')}</th>
              <th className="p-4 border-b border-white/5">{t('unitControl.inspection.column')}</th>
              <th className="p-4 border-b border-white/5 text-right">{t('units.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading && units.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-12 text-center text-slate-500">{t('units.table.loading')}</td>
              </tr>
            ) : filteredUnits.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-12 text-center text-slate-500 text-lg">
                  <AlertTriangle className="mx-auto mb-2 opacity-20" size={48} />
                  {t('units.table.noUnits')}
                </td>
              </tr>
            ) : filteredUnits.map((unit) => (
              <tr
                key={unit.id}
                className={`border-b border-white/5 hover:bg-white/5 transition-all group ${
                  unit._justUpdated ? 'bg-cyan-500/10' : ''
                }`}
              >
                <td className="p-4">
                   <Link
                    to={`/units/${unit.id}`}
                    className="font-mono font-bold text-cyan-400 hover:underline"
                    title={unit.deviceId ? `Full device ID: ${unit.deviceId}` : undefined}
                  >
                    {formatStationId(unit)}
                  </Link>
                  {formatListDeviceName(unit) && (
                    <Link
                      to={`/units/${unit.id}`}
                      className="mt-2 block text-xs font-medium text-white transition-colors hover:text-cyan-200 hover:underline"
                    >
                      {formatListDeviceName(unit)}
                    </Link>
                  )}
                </td>
                <td className="p-4">{renderPartyCell(getUnitPartner(unit))}</td>
                <td className="p-4">{renderPartyCell(getUnitRegionalPartner(unit))}</td>
                <td className="p-4">{renderPartyCell(getUnitSteward(unit))}</td>
                <td className="p-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[10px] uppercase tracking-wide text-slate-500">
                        {t('units.table.mineralWater')}
                      </span>
                      <span className="text-xs font-bold text-cyan-300">
                        {formatWaterPrice(unit.mineralWaterPrice)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t border-white/5 pt-2">
                      <span className="text-[10px] uppercase tracking-wide text-slate-500">
                        {t('units.table.pureWater')}
                      </span>
                      <span className="text-xs font-bold text-emerald-300">
                        {formatWaterPrice(unit.pureWaterPrice)}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  {renderUnitStatusCell(unit)}
                </td>
                <td className="p-4">
                  <div className="space-y-2">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold ${
                      unit.inspectedToday
                        ? 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                        : 'border border-slate-600 bg-slate-700/60 text-slate-300'
                    }`}>
                      {unit.inspectedToday ? t('unitControl.inspection.checkedToday') : t('unitControl.inspection.notCheckedToday')}
                    </span>
                    <div className="text-[11px] font-medium text-slate-300">
                      {t('unitControl.inspection.latest')}: {formatInspectionDateTime(unit.latestInspectionAt, i18n.language) || t('unitControl.inspection.none')}
                    </div>
                    <div className="text-[11px] font-medium text-slate-300">
                      {t('unitControl.inspection.reviewStatus')}: {unit.latestInspectionReviewStatus
                        ? t(`unitControl.inspection.review.${unit.latestInspectionReviewStatus}`, { defaultValue: unit.latestInspectionReviewStatus })
                        : t('unitControl.inspection.none')}
                    </div>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <div className="flex min-w-[48px] items-center justify-end">
                  {canOpenUnitEditor && <button
                    onClick={() => handleEditUnit(unit)}
                    className="p-1.5 text-slate-500 hover:text-orange-400 transition-colors"
                    title={t('unitControl.editUnitAction')}
                  >
                    <Edit size={16} />
                  </button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Import Device Dialog */}
      {canEditUnits && showImportDialog && (
        <AdminModalSurface onClose={() => setShowImportDialog(false)}>
          <div className="bg-slate-800 rounded-3xl p-8 w-full max-w-xl border border-white/10 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Upload size={24} className="text-cyan-400" />
                {t('unitControl.importTitle')}
              </h3>
              <button
                onClick={() => {
                  setShowImportDialog(false);
                  setImportDeviceNo('');
                  setImportMessage(null);
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-300 mb-2">
                  {t('unitControl.batchDeviceNo')}
                </label>
                <textarea
                  value={importDeviceNo}
                  onChange={(e) => { setImportDeviceNo(e.target.value); setImportPreview(null); }}
                  placeholder={t('unitControl.batchDeviceNoPlaceholder')}
                  className="min-h-[140px] w-full resize-y bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50"
                  disabled={importLoading}
                  autoFocus
                />
              </div>

              {importPreview && <div className="max-h-48 overflow-auto rounded-xl border border-cyan-500/30 p-3 text-sm text-white">
                <p>{t('unitControl.importPreview', { count: importPreview.rows.length })}</p>
                {importPreview.rows.slice(0, 20).map((row) => <p className="break-all font-mono" key={row.deviceNo}>{row.deviceNo}{row.deviceName ? ` · ${row.deviceName}` : ''}</p>)}
                {importPreview.rows.some((row) => /^\d+(\.\d+)?[eE][+-]?\d+$/.test(row.deviceNo)) && <p className="text-amber-300">{t('unitControl.scientificIdWarning')}</p>}
              </div>}
              {/* Import Message */}
              {(identityDiagnostics.length > 0 || diagnosticsError) && <div className="max-h-40 overflow-auto rounded-xl border border-white/10 p-3 text-xs text-slate-300">
                <p>{t('unitControl.identityDiagnostics')}</p>
                {diagnosticsError ? <p>{t('unitControl.diagnosticsUnavailable')}</p> : identityDiagnostics.slice(0, 10).map((entry) =>
                  <p className="break-all" key={entry.rawDId}>{entry.rawDId} → {entry.registeredDeviceId || t('unitControl.identityNeedsReview')} · {entry.code || t('unitControl.identityAuthPassed')}</p>)}
              </div>}
              {importMessage && (
                <div className={`flex items-start gap-3 p-4 rounded-xl ${
                  importMessage.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                  importMessage.type === 'error' ? 'bg-rose-500/10 border border-rose-500/30' :
                  'bg-amber-500/10 border border-amber-500/30'
                }`}>
                  {importMessage.type === 'success' ? (
                    <CheckCircle2 size={20} className="text-emerald-400 shrink-0 mt-0.5" />
                  ) : importMessage.type === 'error' ? (
                    <AlertTriangle size={20} className="text-rose-400 shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
                  )}
                  <p className={`text-sm font-semibold ${
                    importMessage.type === 'success' ? 'text-emerald-300' :
                    importMessage.type === 'error' ? 'text-rose-300' :
                    'text-amber-300'
                  }`}>
                    {importMessage.text}
                  </p>
                </div>
              )}

              {/* Info */}
              <div className="flex flex-col gap-3 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-xl sm:flex-row sm:items-start sm:justify-between">
                <p className="text-xs text-cyan-300 leading-relaxed">
                  {t('unitControl.importInfo')}
                </p>
                <button
                  type="button"
                  onClick={downloadImportTemplate}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-cyan-400/40 px-3 py-2 text-xs font-bold text-cyan-200 hover:bg-cyan-400/10 transition-colors"
                >
                  <Upload size={14} />
                  {t('unitControl.downloadTemplate')}
                </button>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportDeviceNo('');
                    setImportMessage(null);
                  }}
                  disabled={importLoading}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-700/50 text-white font-bold py-3 px-4 rounded-xl transition-all"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleRegisterDevice}
                  disabled={importLoading || !importDeviceNo.trim()}
                  className={`flex-1 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
                    importLoading || !importDeviceNo.trim()
                      ? 'bg-white/10 text-slate-500 cursor-not-allowed'
                      : 'bg-cyan-400 text-slate-900 hover:bg-cyan-300'
                  }`}
                >
                  {importLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t('unitControl.importing')}
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      {t(importPreview ? 'unitControl.confirmImport' : 'unitControl.previewImport')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </AdminModalSurface>
      )}
      {/* Edit Unit Dialog */}
      {canOpenUnitEditor && showEditUnitDialog && selectedUnitForEdit && (
        <AdminModalSurface onClose={() => setShowEditUnitDialog(false)}>
          <div className="bg-slate-800 rounded-3xl p-8 w-full max-w-4xl max-h-[92vh] overflow-y-auto border border-white/10 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Edit size={24} className="text-orange-400" />
                {t('unitControl.editUnitTitle')} <span className="font-mono text-cyan-400 text-sm" title={selectedUnitForEdit.deviceId}>{formatStationId(selectedUnitForEdit)}</span>
              </h3>
              <button
                onClick={() => setShowEditUnitDialog(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {canEditUnits && <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black flex items-center gap-1 mb-2">
                    <CalendarDays size={12} />
                    {t('unitControl.installedAtLabel', { defaultValue: 'Real install date' })}
                  </label>
                  <input
                    type="date"
                    value={editForm.installedAt}
                    onChange={(e) => setEditForm(f => ({ ...f, installedAt: e.target.value }))}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                    title={t('unitControl.installedAtTitle', { defaultValue: 'Date when the device was actually installed at the customer site' })}
                  />
                </div>

                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black flex items-center gap-1 mb-2">
                    <MapPin size={12} />
                    {t('unitControl.fullAddressLabel', { defaultValue: 'Full station address' })}
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <textarea
                      rows={3}
                      value={editForm.location}
                      onChange={(e) => setEditForm(f => ({ ...f, location: e.target.value }))}
                      onBlur={() => handleApplyAddressSplit()}
                      placeholder={t('unitControl.fullAddressPlaceholder', { defaultValue: 'Paste the full Philippine address here' })}
                      className="min-h-[88px] flex-1 resize-none bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleApplyAddressSplit()}
                      className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 text-sm font-bold text-cyan-200 hover:bg-cyan-400/20 sm:h-auto"
                      title={t('unitControl.splitAddressTitle', { defaultValue: 'Split full address into province, city or municipality, barangay, and detail fields' })}
                    >
                      <Wand2 size={16} />
                      {t('unitControl.splitAddressAction', { defaultValue: 'Split' })}
                    </button>
                  </div>
                </div>
              </div>

              {canEditUnits && <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black flex items-center gap-1 mb-2">
                    <Monitor size={12} />
                    {t('unitControl.simCardNumberLabel', { defaultValue: 'SIM card number' })}
                  </label>
                  <input
                    type="text"
                    value={editForm.simCardNumber}
                    disabled={!canManageSim || selectedUnitForEdit.simSchemaReady !== true}
                    onChange={(e) => setEditForm(f => ({ ...f, simCardNumber: e.target.value, protocolDId: !f.protocolDId || f.protocolDId === f.simCardNumber ? e.target.value : f.protocolDId }))}
                    placeholder={t('unitControl.simCardNumberPlaceholder', { defaultValue: 'ICCID, SIM number, or phone number' })}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                    title={t('unitControl.simCardNumberTitle', { defaultValue: 'SIM card number used by the device modem' })}
                  />
                </div>

                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black flex items-center gap-1 mb-2">
                    <CalendarDays size={12} />
                    {t('unitControl.simPaidUntilLabel', { defaultValue: 'SIM paid until' })}
                  </label>
                  <input
                    type="date"
                    value={editForm.simPaidUntil}
                    disabled={!canManageSim || selectedUnitForEdit.simSchemaReady !== true}
                    onChange={(e) => setEditForm(f => ({ ...f, simPaidUntil: e.target.value }))}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                    title={t('unitControl.simPaidUntilTitle', { defaultValue: 'Date until which the SIM service is paid and expected to stay active' })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {['simSupplier', 'protocolDId', 'legacyQrAlias'].map((key) => <label className="text-sm text-slate-300" key={key}>
                  {t(`unitControl.${key}`)}
                  <input type="text" aria-label={t(`unitControl.${key}`)} value={editForm[key]} disabled={!canManageSim || selectedUnitForEdit.simSchemaReady !== true}
                    onChange={(event) => setEditForm((form) => ({ ...form, [key]: event.target.value }))}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-slate-700/50 px-4 py-3 text-white disabled:opacity-50" />
                </label>)}
              </div>
              {selectedUnitForEdit.simSchemaReady !== true && <p className="text-sm text-amber-300">{t(selectedUnitForEdit.simAccessDenied ? 'unitControl.simAccessDenied' : 'unitControl.simUnavailable')}</p>}
              </>}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">
                    {t('unitControl.provinceNameLabel', { defaultValue: 'Province' })}
                  </label>
                  <input
                    type="text"
                    value={editForm.provinceName}
                    onChange={(e) => setEditForm(f => ({ ...f, provinceCode: '', provinceName: e.target.value }))}
                    placeholder={t('unitControl.locationPlaceholders.province', { defaultValue: 'Province' })}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">
                    {t('unitControl.cityNameLabel', { defaultValue: 'City / Municipality' })}
                  </label>
                  <input
                    type="text"
                    value={editForm.cityName}
                    onChange={(e) => setEditForm(f => ({ ...f, cityName: e.target.value }))}
                    placeholder={t('unitControl.locationPlaceholders.city', { defaultValue: 'City or municipality' })}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">
                    {t('unitControl.districtNameLabel', { defaultValue: 'Barangay' })}
                  </label>
                  <input
                    type="text"
                    value={editForm.districtName}
                    onChange={(e) => setEditForm(f => ({ ...f, districtName: e.target.value }))}
                    placeholder={t('unitControl.locationPlaceholders.district', { defaultValue: 'Barangay' })}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">
                  {t('unitControl.addressDetailLabel', { defaultValue: 'Address detail' })}
                </label>
                <input
                  type="text"
                  value={editForm.addressDetail}
                  onChange={(e) => setEditForm(f => ({ ...f, addressDetail: e.target.value }))}
                  placeholder={t('unitControl.locationPlaceholders.addressDetail', { defaultValue: 'Street, building, unit number' })}
                  className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                />
              </div>

              {canManagePartnerBindings && <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">
                    {t('unitControl.kudBinding', { defaultValue: 'Partner PA binding' })}
                  </label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder={t('unitControl.searchKudPlaceholder', { defaultValue: 'Search Partner PA by name or phone' })}
                      value={kudSearchQuery}
                      onChange={(e) => searchKudUsers(e.target.value)}
                      className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                    />
                    {kudOptions.length > 0 && (
                      <div className="border border-white/10 rounded-xl bg-slate-900/50 max-h-48 overflow-y-auto">
                        {kudOptions.map((user) => (
                          <div
                            key={user.id}
                            onClick={() => {
                              setEditForm(f => ({ ...f, rpOwnerId: user.id }));
                              setKudSearchQuery(`${getUserDisplayName(user)} (ID: ${user.id})`);
                              setKudOptions([]);
                            }}
                            className="p-3 hover:bg-slate-800 cursor-pointer border-b border-white/5 last:border-b-0"
                          >
                            <p className="text-sm font-bold text-white">{getUserDisplayName(user)}</p>
                            <p className="text-xs text-slate-300">
                              {t('unitControl.userOptionMeta', {
                                id: user.id,
                                account: getUserAccount(user) || '-',
                                phone: user.phoneNumber || user.phone || '-',
                                role: getUserRoleSummary(user) || '-'
                              })}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    {editForm.rpOwnerId && (
                      <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3">
                        <p className="text-sm text-amber-400">{t('unitControl.selectedId', { id: editForm.rpOwnerId })}</p>
                        <button
                          type="button"
                          onClick={() => {
                            setEditForm(f => ({ ...f, rpOwnerId: '' }));
                            setKudSearchQuery('');
                          }}
                          className="rounded-lg p-1 text-amber-300 hover:bg-amber-500/20"
                          title={t('common.clear', { defaultValue: 'Clear' })}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">
                    {t('unitControl.puskudBinding', { defaultValue: 'Regional Partner RP binding' })}
                  </label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder={t('unitControl.searchPuskudPlaceholder', { defaultValue: 'Search Regional Partner RP by name or phone' })}
                      value={puskudSearchQuery}
                      onChange={(e) => searchPuskudUsers(e.target.value)}
                      className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                    />
                    {puskudOptions.length > 0 && (
                      <div className="border border-white/10 rounded-xl bg-slate-900/50 max-h-48 overflow-y-auto">
                        {puskudOptions.map((user) => (
                          <div
                            key={user.id}
                            onClick={() => {
                              setEditForm(f => ({ ...f, puskudId: user.id }));
                              setPuskudSearchQuery(`${getUserDisplayName(user)} (ID: ${user.id})`);
                              setPuskudOptions([]);
                            }}
                            className="p-3 hover:bg-slate-800 cursor-pointer border-b border-white/5 last:border-b-0"
                          >
                            <p className="text-sm font-bold text-white">{getUserDisplayName(user)}</p>
                            <p className="text-xs text-slate-300">
                              {t('unitControl.userOptionMeta', {
                                id: user.id,
                                account: getUserAccount(user) || '-',
                                phone: user.phoneNumber || user.phone || '-',
                                role: getUserRoleSummary(user) || '-'
                              })}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    {editForm.puskudId && (
                      <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3">
                        <p className="text-sm text-amber-400">{t('unitControl.selectedId', { id: editForm.puskudId })}</p>
                        <button
                          type="button"
                          onClick={() => {
                            setEditForm(f => ({ ...f, puskudId: '' }));
                            setPuskudSearchQuery('');
                          }}
                          className="rounded-lg p-1 text-amber-300 hover:bg-amber-500/20"
                          title={t('common.clear', { defaultValue: 'Clear' })}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">{t('unitControl.stewardBinding')}</label>
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder={t('unitControl.searchStewardPlaceholder')}
                    value={stewardSearchQuery}
                    onChange={(e) => searchStewardUsers(e.target.value)}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                  {stewardOptions.length > 0 && (
                    <div className="border border-white/10 rounded-xl bg-slate-900/50 max-h-48 overflow-y-auto">
                      {stewardOptions.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => {
                            setEditForm(f => ({ ...f, stewardId: user.id }));
                            setStewardSearchQuery(`${getUserDisplayName(user)} (ID: ${user.id})`);
                            setStewardOptions([]);
                          }}
                          className="p-3 hover:bg-slate-800 cursor-pointer border-b border-white/5 last:border-b-0"
                        >
                          <p className="text-sm font-bold text-white">{getUserDisplayName(user)}</p>
                          <p className="text-xs text-slate-300">
                            {t('unitControl.userOptionMeta', {
                              id: user.id,
                              account: getUserAccount(user) || '-',
                              phone: user.phoneNumber || user.phone || '-',
                              role: getUserRoleSummary(user) || '-'
                            })}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {editForm.stewardId && (
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3">
                      <p className="text-sm text-amber-400">{t('unitControl.selectedId', { id: editForm.stewardId })}</p>
                      <button
                        type="button"
                        onClick={() => {
                          setEditForm(f => ({ ...f, stewardId: '' }));
                          setStewardSearchQuery('');
                        }}
                        className="rounded-lg p-1 text-amber-300 hover:bg-amber-500/20"
                        title={t('common.clear', { defaultValue: 'Clear' })}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              </>}
              </>}

              {canManageUnitPricing && (
              <section className="space-y-4 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4">
                <div>
                  <h4 className="text-sm font-black text-amber-200">{t('unitControl.pricingSectionTitle', { defaultValue: 'Water pricing' })}</h4>
                  <p className="mt-1 text-xs text-amber-100/70">{t('unitControl.pricingSectionHint', { defaultValue: 'This is a separate high-risk permission. Saving prices does not change any other station data.' })}</p>
                  {waterPriceConfigurationState === 'loading' && (
                    <p className="mt-2 text-xs text-cyan-200">{t('unitControl.pricingConfigLoading', { defaultValue: 'Loading saved water-price limits…' })}</p>
                  )}
                  {waterPriceConfigurationState === 'error' && (
                    <p className="mt-2 text-xs font-bold text-red-300">{t('unitControl.pricingConfigUnavailable', { defaultValue: 'Water-price limits are unavailable. Configure and save the 20 PHP amounts in System Settings, then reload this page.' })}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">{t('unitControl.mineralWaterPriceLabel')}</label>
                  <input
                    type="number"
                    value={editForm.mineralWaterPrice}
                    onChange={(e) => setEditForm(f => ({ ...f, mineralWaterPrice: e.target.value }))}
                    placeholder={waterPriceConfiguration ? String(waterPriceConfiguration.defaults.mineral) : ''}
                    min={mineralWaterPriceLimits?.min}
                    max={mineralWaterPriceLimits?.max}
                    step="0.01"
                    disabled={!waterPriceConfiguration}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                  {mineralWaterPriceLimits && <p className="mt-2 text-xs text-slate-500">{t('unitControl.priceRangeHint', { min: mineralWaterPriceLimits.min, max: mineralWaterPriceLimits.max })}</p>}
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">{t('unitControl.pureWaterPriceLabel')}</label>
                  <input
                    type="number"
                    value={editForm.pureWaterPrice}
                    onChange={(e) => setEditForm(f => ({ ...f, pureWaterPrice: e.target.value }))}
                    placeholder={waterPriceConfiguration ? String(waterPriceConfiguration.defaults.pure) : ''}
                    min={pureWaterPriceLimits?.min}
                    max={pureWaterPriceLimits?.max}
                    step="0.01"
                    disabled={!waterPriceConfiguration}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                  {pureWaterPriceLimits && <p className="mt-2 text-xs text-slate-500">{t('unitControl.priceRangeHint', { min: pureWaterPriceLimits.min, max: pureWaterPriceLimits.max })}</p>}
                </div>
                </div>
              </section>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEditUnitDialog(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 px-4 rounded-xl transition-all"
                >
                  {t('common.cancel')}
                </button>
                {canEditUnits && <button
                  onClick={handleSaveUnitDetails}
                  disabled={editLoading}
                  className={`flex-1 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
                    editLoading ? 'bg-white/10 text-slate-500 cursor-not-allowed' : 'bg-orange-500 text-white hover:bg-orange-400'
                  }`}
                >
                  {editLoading ? <><Loader2 size={18} className="animate-spin" />{t('unitControl.savingChanges')}</> : <><Save size={18} />{t('unitControl.saveChanges')}</>}
                </button>}
                {canManageUnitPricing && <button
                  onClick={handleSaveUnitPricing}
                  disabled={editLoading || !waterPriceConfiguration}
                  className={`flex-1 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
                    editLoading || !waterPriceConfiguration ? 'bg-white/10 text-slate-500 cursor-not-allowed' : 'bg-amber-500 text-slate-950 hover:bg-amber-400'
                  }`}
                >
                  {editLoading ? <><Loader2 size={18} className="animate-spin" />{t('unitControl.savingChanges')}</> : <><Save size={18} />{t('unitControl.savePricing', { defaultValue: 'Save water pricing' })}</>}
                </button>}
              </div>
            </div>
          </div>
        </AdminModalSurface>
      )}
    </div>
  );
}
